import {
  Button,
  Input,
  Spacer,
  Popover,
  Alert,
  Text,
  List,
  Stack,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useState } from "react";
import { t } from "i18next";
import { useStores } from "../../../../shared/stores/RootStore";
import { DeleteIcon } from "../../../../shared/views/icons";
import { CustomDialog } from "../../../../shared/views/CustomDialog";
import { CustomPopover } from "../../../../shared/views/CustomPopover";
import { toast } from "../../../../shared/views/CustomToaster";

const CampaignManagementModalBase: React.FC<{
  onClose: () => void;
}> = ({ onClose }) => {
  const { campaignStore, osaWayStore, hlaStore } = useStores();
  const selectedCampaign = campaignStore.selected!;
  const [name, setName] = useState(selectedCampaign.name);
  const [submitting, setSubmitting] = useState(false);
  const isDirty = selectedCampaign.name !== name;
  const isEmpty = !name;

  const nOsaWays = osaWayStore.ways.reduce(
    (count, osaWay) =>
      osaWay.campaignId === selectedCampaign.id ? count + 1 : count,
    0
  );

  const nHlaWays = hlaStore.ways.reduce(
    (count, hlaWay) =>
      hlaWay.campaignId === selectedCampaign.id ? count + 1 : count,
    0
  );

  const submitUpdate = () => {
    setSubmitting(true);
    campaignStore
      .update(selectedCampaign.id, { name })
      .then(() => {
        toast("success", "Campaign updated");
        onClose();
      })
      .catch(() => {
        toast("error", "Campaign update failed");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const submitDelete = () => {
    setSubmitting(true);
    campaignStore
      .delete(selectedCampaign.id)
      .then(() => {
        toast("success", "Campaign deleted");
        onClose();
      })
      .catch(() => {
        toast("error", "Campaign deletion failed");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <CustomDialog
      onClose={onClose}
      header={`Edit campaign`}
      footer={
        <>
          <CustomPopover
            trigger={
              <Button
                loading={submitting}
                colorPalette="red"
                variant="outline"
                disabled={campaignStore.campaigns.length <= 1}
              >
                <DeleteIcon /> Delete
              </Button>
            }
          >
            <Popover.Header fontSize={"lg"} fontWeight={"semibold"}>
              Are you sure?
            </Popover.Header>
            <Popover.Body>
              {nOsaWays > 0 || nHlaWays > 0 ? (
                <Alert.Root status="warning" fontSize={"sm"}>
                  <Alert.Indicator />
                  <Stack>
                    <Alert.Title>
                      The following dependencies will be deleted
                    </Alert.Title>
                    <Alert.Description>
                      <List.Root>
                        <List.Item>
                          {nOsaWays} on-site assessment{" "}
                          {t("home:way", { count: nOsaWays })}
                        </List.Item>
                        <List.Item>
                          {nHlaWays} remote assessment{" "}
                          {t("home:way", { count: nHlaWays })}
                        </List.Item>
                      </List.Root>
                    </Alert.Description>
                  </Stack>
                </Alert.Root>
              ) : (
                <Text fontSize="sm">
                  This campaign does not have any dependencies.
                </Text>
              )}
            </Popover.Body>
            <Popover.Footer display="flex" justifyContent="end">
              <Button
                onClick={submitDelete}
                colorPalette="red"
                loading={submitting}
              >
                Delete
              </Button>
            </Popover.Footer>
          </CustomPopover>

          <Spacer />
          <Button
            colorPalette="primary"
            disabled={!isDirty || isEmpty}
            onClick={submitUpdate}
            loading={submitting}
          >
            Save
          </Button>
        </>
      }
    >
      <Input
        value={name}
        onChange={(e) => {
          const value = e.target.value;
          setName(value);
        }}
      />
    </CustomDialog>
  );
};

export const CampaignManagementModal = observer(CampaignManagementModalBase);
